import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, ErrorState,
    CopperButton, SlateButton, Field, Select, Pill,
} from "@/components/common/UI";
import {
    UploadCloud, FileSpreadsheet, ArrowRight, X, Loader2,
    CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Download,
} from "lucide-react";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SupplierCompare() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [customers, setCustomers] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [detectedCustomer, setDetectedCustomer] = useState(null);
    const [file, setFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef();
    const [uploading, setUploading] = useState(false);
    const [job, setJob] = useState(null);
    const [report, setReport] = useState(null);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [supplierName, setSupplierName] = useState("");
    const [quarter, setQuarter] = useState("");
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        api.get("/customers").then((r) => setCustomers(r.data)).catch((e) => toast.error(formatApiError(e)));
        api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    }, []);

    // resume existing job via ?job=<id>
    useEffect(() => {
        const j = searchParams.get("job");
        if (j) loadJob(j);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadJob = async (jobId) => {
        try {
            const { data } = await api.get(`/zbc/supplier/jobs/${jobId}`);
            setJob(data.job);
            setReport(data.report);
            setSelectedIdx(0);
            setSupplierName(data.job?.supplier_name || "");
            setQuarter(data.job?.quarter || "");
            if (data.job?.customer_auto_detected || data.job?.customer_auto_created) {
                setDetectedCustomer({
                    name: data.job.customer_name,
                    code: data.job.customer_code,
                    autoDetected: !!data.job.customer_auto_detected,
                    autoCreated: !!data.job.customer_auto_created,
                });
            } else {
                setDetectedCustomer(null);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const downloadExcel = async () => {
        if (!job) return;
        // Save meta first (supplier_name + quarter)
        try {
            if (supplierName !== (job.supplier_name || "") || quarter !== (job.quarter || "")) {
                await api.put(`/zbc/supplier/jobs/${job.id}/meta`, {
                    supplier_name: supplierName, quarter,
                });
            }
        } catch (e) {
            toast.error(formatApiError(e));
            return;
        }
        setDownloading(true);
        try {
            const resp = await api.get(`/zbc/supplier/jobs/${job.id}/export.xlsx`, {
                responseType: "blob",
            });
            const blobUrl = URL.createObjectURL(resp.data);
            const cd = resp.headers["content-disposition"] || "";
            const m = /filename="?([^";]+)"?/.exec(cd);
            const filename = m ? m[1] : `CostIQ_Export_${job.id.slice(0, 8)}.xlsx`;
            const a = document.createElement("a");
            a.href = blobUrl; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(blobUrl);
            toast.success("Excel downloaded");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDownloading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const dropped = Array.from(e.dataTransfer.files || [])[0];
        if (dropped && dropped.name.toLowerCase().endsWith(".xlsx")) setFile(dropped);
        else toast.error("Only .xlsx accepted");
    };

    const upload = async () => {
        if (!file) return toast.error("Add an .xlsx workbook");
        setUploading(true);
        try {
            const fd = new FormData();
            if (customerId) fd.append("customer_id", customerId);
            fd.append("files", file);
            const { data } = await api.post("/zbc/supplier/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 3 * 60 * 1000,
            });
            toast.success(`Parsed · ${data.part_count} part(s) · ${data.matched_count} matched`);
            if (data.customer?.auto_created) {
                toast.message(`New customer "${data.customer.name}" auto-created, review it in Customers & Norms`, { duration: 8000 });
            } else if (data.customer?.auto_detected) {
                toast.message(`Matched to customer "${data.customer.name}"`, { duration: 5000 });
            }
            await loadJob(data.job_id);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
        }
    };

    const selectedPart = report?.parts?.[selectedIdx];

    return (
        <div data-testid="supplier-compare">
            <PageHeader
                eyebrow="ZBC · SUPPLIER COMPARE"
                title="Supplier Workbook Comparison"
                subtitle="Upload the vendor's ZBC workbook (.xlsx). CostIQ auto-parses every part sheet and compares each line item against the ABC COMPANY ZBC (Intelligent-generated or manual) for the same part number, with severity-flagged deltas + financial impact."
                actions={<SlateButton variant="outline" onClick={() => navigate("/zbc")}>← Back to ZBC List</SlateButton>}
            />

            {!report && (
                <Card>
                    <div className="p-5 space-y-5">
                        <Field label="Customer (optional, auto-detected from the workbook's part numbers if left blank)">
                            <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="sup-customer">
                                <option value="">Auto-detect from part numbers…</option>
                                {customers.map((c) => (
                                    <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
                                ))}
                            </Select>
                        </Field>

                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClick={() => inputRef.current?.click()}
                            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                                dragOver
                                    ? "border-[hsl(var(--copper))] bg-amber-50/60"
                                    : "border-slate-300 hover:border-[hsl(var(--copper))] hover:bg-slate-50"
                            }`}
                            data-testid="sup-dropzone"
                        >
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".xlsx"
                                hidden
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                            />
                            <UploadCloud className="w-10 h-10 mx-auto text-[hsl(var(--copper))] mb-2" />
                            <div className="text-[15px] font-semibold text-slate-800">
                                Drop supplier ZBC .xlsx here or click to browse
                            </div>
                            <div className="text-[12px] text-slate-500 mt-1">
                                Standard ABC COMPANY supplier template (Summary + per-part sheets).
                            </div>
                        </div>

                        {file && (
                            <div className="flex items-center justify-between px-3 py-2 rounded border border-slate-200 bg-white text-[13px]">
                                <div className="flex items-center gap-2">
                                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                                    <span>{file.name}</span>
                                    <span className="text-slate-400 text-[11px]">
                                        {(file.size / 1024).toFixed(1)} KB
                                    </span>
                                </div>
                                <button onClick={() => setFile(null)} className="p-1 hover:bg-slate-100 rounded">
                                    <X className="w-3.5 h-3.5 text-slate-400" />
                                </button>
                            </div>
                        )}

                        <div className="flex justify-end">
                            <CopperButton onClick={upload} disabled={uploading || !file} data-testid="sup-upload-btn">
                                {uploading
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing & comparing…</>
                                    : <>Parse & Compare <ArrowRight className="w-4 h-4" /></>
                                }
                            </CopperButton>
                        </div>
                    </div>
                </Card>
            )}

            {report && (
                <>
                    {detectedCustomer && <DetectedCustomerBanner c={detectedCustomer} />}
                    <SummaryKPIs report={report} />
                    <Card className="mt-4">
                        <div className="p-4 flex items-end gap-4">
                            <div className="flex-1 grid grid-cols-2 gap-3">
                                <Field label="Supplier (for Excel header)" helper="Picked from the Suppliers master, Foundation Setup › Suppliers">
                                    <Select
                                        value={supplierName}
                                        onChange={(e) => setSupplierName(e.target.value)}
                                        data-testid="sup-name-input"
                                    >
                                        <option value="">Select supplier…</option>
                                        {suppliers.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                                    </Select>
                                </Field>
                                <Field label="Quarter (for Excel header)">
                                    <input
                                        className="w-full px-3 py-1.5 rounded border border-slate-300 text-[13px]"
                                        value={quarter}
                                        onChange={(e) => setQuarter(e.target.value)}
                                        placeholder="e.g. Q1 FY26-27"
                                        data-testid="sup-quarter-input"
                                    />
                                </Field>
                            </div>
                            <CopperButton
                                onClick={downloadExcel}
                                disabled={downloading || (report.matched_count || 0) === 0}
                                data-testid="sup-download-btn"
                                title={
                                    (report.matched_count || 0) === 0
                                        ? "No matched parts: create Intelligent ZBCs first"
                                        : "Download Observations + Financial Impact"
                                }
                            >
                                {downloading
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</>
                                    : <><Download className="w-4 h-4" /> Download Excel (.xlsx)</>
                                }
                            </CopperButton>
                        </div>
                    </Card>
                    <div className="grid grid-cols-[300px_1fr] gap-4 mt-4">
                        <PartList report={report} selectedIdx={selectedIdx} onSelect={setSelectedIdx} />
                        {selectedPart && <PartDetail part={selectedPart} />}
                    </div>
                </>
            )}
        </div>
    );
}

// ------------------- Auto-detected/created customer banner -------------------
function DetectedCustomerBanner({ c }) {
    if (c.autoCreated) {
        return (
            <Card className="mb-4 border-amber-300">
                <div className="p-3 flex items-start gap-2 bg-amber-50/60">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-[12.5px] text-slate-700">
                        <span className="font-semibold">New customer auto-created: {c.code} · {c.name}.</span>{" "}
                        No existing ZBC matched any part number in this workbook, and supplier workbooks
                        don't carry an OEM customer name: this name was guessed from the filename.
                        Review and correct it in <span className="font-semibold">Customers &amp; Norms</span> before
                        relying on this comparison for pricing decisions.
                    </div>
                </div>
            </Card>
        );
    }
    return (
        <Card className="mb-4 border-emerald-200">
            <div className="p-3 flex items-center gap-2 bg-emerald-50/50">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <div className="text-[12.5px] text-slate-700">
                    Auto-matched to customer <span className="font-semibold">{c.code} · {c.name}</span> based
                    on existing ZBCs sharing part numbers with this workbook.
                </div>
            </div>
        </Card>
    );
}

// ------------------- KPIs -------------------
function SummaryKPIs({ report }) {
    const t = report.totals || {};
    const deltaTone = (t.delta || 0) > 0 ? "amber" : (t.delta || 0) < 0 ? "emerald" : "slate";
    return (
        <div className="grid grid-cols-4 gap-3">
            <Card>
                <div className="p-4">
                    <div className="eyebrow text-slate-500 mb-1">Parts detected</div>
                    <div className="text-2xl font-semibold font-mono">{report.part_count}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{report.filename}</div>
                </div>
            </Card>
            <Card>
                <div className="p-4">
                    <div className="eyebrow text-slate-500 mb-1">Matched</div>
                    <div className="text-2xl font-semibold font-mono text-emerald-600">{report.matched_count}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">against ABC COMPANY ZBCs</div>
                </div>
            </Card>
            <Card>
                <div className="p-4">
                    <div className="eyebrow text-slate-500 mb-1">Unmatched</div>
                    <div className="text-2xl font-semibold font-mono text-amber-600">{report.unmatched_count}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">need manual mapping</div>
                </div>
            </Card>
            <Card>
                <div className="p-4">
                    <div className="eyebrow text-slate-500 mb-1">Total Δ (Supplier − ABC COMPANY)</div>
                    <div className="flex items-center gap-2">
                        {t.delta > 0 ? <TrendingUp className="w-5 h-5 text-amber-600" /> :
                         t.delta < 0 ? <TrendingDown className="w-5 h-5 text-emerald-600" /> : null}
                        <div className={`text-2xl font-semibold font-mono ${
                            deltaTone === "amber" ? "text-amber-600" :
                            deltaTone === "emerald" ? "text-emerald-600" : "text-slate-800"
                        }`}>{fmtINR(t.delta || 0, 2)}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                        Supplier ₹{fmtINR(t.supplier || 0, 0)} · ABC COMPANY ₹{fmtINR(t.surin || 0, 0)}
                    </div>
                </div>
            </Card>
        </div>
    );
}

// ------------------- Part list sidebar -------------------
function PartList({ report, selectedIdx, onSelect }) {
    return (
        <Card>
            <div className="p-3">
                <div className="eyebrow text-slate-500 mb-2 px-1">Parts in workbook</div>
                <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                    {report.parts.map((p, i) => {
                        const worst = worstSeverity(p.rows);
                        const badge = worst === "CRITICAL" ? "red" : worst === "WARN" ? "amber" : "emerald";
                        return (
                            <button
                                key={i}
                                onClick={() => onSelect(i)}
                                className={`w-full text-left px-3 py-2 rounded text-[12.5px] transition-colors ${
                                    selectedIdx === i
                                        ? "bg-[hsl(var(--copper))]/10 border border-[hsl(var(--copper))]/30"
                                        : "hover:bg-slate-50 border border-transparent"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-mono font-semibold truncate">
                                            {p.supplier_part.part_number || p.supplier_part.sheet_name}
                                        </div>
                                        <div className="text-[11px] text-slate-500 truncate">{p.supplier_part.part_description}</div>
                                    </div>
                                    {p.match_status === "MATCHED" ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                    <Pill tone={p.match_status === "MATCHED" ? "emerald" : "amber"}>{p.match_status}</Pill>
                                    {p.match_status === "MATCHED" && <Pill tone={badge}>{worst}</Pill>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </Card>
    );
}

function worstSeverity(rows) {
    let worst = null;
    for (const r of rows || []) {
        if (r.severity === "CRITICAL") return "CRITICAL";
        if (r.severity === "WARN") worst = "WARN";
        if (!worst && r.severity === "INFO") worst = "INFO";
    }
    return worst;
}

// ------------------- Part detail: side-by-side -------------------
function PartDetail({ part }) {
    const fi = part.financial_impact || {};
    return (
        <div className="space-y-4">
            <Card>
                <div className="p-4 border-b border-slate-100">
                    <div className="text-[13px] font-semibold text-slate-800">{part.supplier_part.part_number || part.supplier_part.sheet_name}</div>
                    <div className="text-[12px] text-slate-500">{part.supplier_part.part_description}</div>
                </div>
                {part.match_status === "UNMATCHED" ? (
                    <div className="p-4">
                        <ErrorState
                            message={`No ABC COMPANY ZBC found for part '${part.supplier_part.part_number || ""}'. Create an Intelligent ZBC or Manual ZBC for this part number first, then re-upload.`}
                        />
                    </div>
                ) : (
                    <>
                        <div className="p-4 grid grid-cols-3 gap-3 bg-slate-50/60 border-b border-slate-100">
                            <FiBox label="Supplier Net" v={fi.supplier_net} tone="slate" />
                            <FiBox label="ABC COMPANY Net (Independent)" v={fi.surin_net} tone="slate" />
                            <FiBox label="Δ per piece (Full)" v={fi.delta} pct={fi.delta_pct}
                                   tone={fi.delta > 0 ? "amber" : fi.delta < 0 ? "emerald" : "slate"} />
                        </div>
                        {fi.rate_only_net != null && (
                            <div className="p-4 grid grid-cols-3 gap-3 bg-slate-50/30 border-b border-slate-100">
                                <FiBox label="ABC COMPANY Net (at Supplier's Weight)" v={fi.rate_only_net} tone="slate" />
                                <FiBox label="Δ per piece (Rate-Only)" v={fi.rate_only_delta} pct={fi.rate_only_delta_pct}
                                       tone={fi.rate_only_delta > 0 ? "amber" : fi.rate_only_delta < 0 ? "emerald" : "slate"} />
                                <div className="text-[11px] text-slate-500 self-center">
                                    {Math.abs(fi.rate_only_delta || 0) < Math.abs(fi.delta || 0) * 0.5
                                        ? "Rate-Only Δ is much smaller than Full Δ: the gap is mostly in the supplier's claimed weight/geometry, not their rates."
                                        : "Rate-Only Δ is close to Full Δ: the gap is mostly a rate/process-norm difference."}
                                </div>
                            </div>
                        )}
                        {fi.annual_saving != null && (
                            <div className="px-4 py-2 border-b border-slate-100 text-[12px] text-slate-600 flex items-center gap-2">
                                <TrendingUp className="w-3.5 h-3.5 text-[hsl(var(--copper))]" />
                                Annual impact @ {fi.monthly_volume}/mo × 12: <span className="font-mono font-semibold">{fmtINR(fi.annual_saving, 0)}</span>
                                {fi.rate_only_annual_saving != null && (
                                    <span className="text-slate-400">
                                        &nbsp;· Rate-Only: <span className="font-mono font-semibold text-slate-600">{fmtINR(fi.rate_only_annual_saving, 0)}</span>
                                    </span>
                                )}
                            </div>
                        )}
                        <div className="overflow-x-auto">
                            <table className="w-full text-[12.5px]">
                                <thead className="bg-white border-b border-slate-200">
                                    <tr>
                                        <th className="text-left py-2 px-3 eyebrow text-slate-500">Metric</th>
                                        <th className="text-right py-2 px-3 eyebrow text-slate-500">Supplier (U)</th>
                                        <th className="text-right py-2 px-3 eyebrow text-slate-500">ABC COMPANY · Independent (CP)</th>
                                        <th className="text-right py-2 px-3 eyebrow text-slate-500">ABC COMPANY · Rate-Only (C)</th>
                                        <th className="text-right py-2 px-3 eyebrow text-slate-500">Δ</th>
                                        <th className="text-right py-2 px-3 eyebrow text-slate-500">Δ %</th>
                                        <th className="text-center py-2 px-3 eyebrow text-slate-500">Flag</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {part.rows.map((r) => (
                                        <tr key={r.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                                            <td className="py-2 px-3">{r.label}</td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.supplier)}</td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums">{fmt(r.surin)}</td>
                                            <td className="py-2 px-3 text-right font-mono tabular-nums text-slate-500">
                                                {r.rate_only != null ? fmt(r.rate_only) : "—"}
                                            </td>
                                            <td className={`py-2 px-3 text-right font-mono tabular-nums ${
                                                r.delta > 0 ? "text-amber-600" : r.delta < 0 ? "text-emerald-600" : "text-slate-500"
                                            }`}>{r.delta != null ? fmt(r.delta) : "—"}</td>
                                            <td className={`py-2 px-3 text-right font-mono tabular-nums ${
                                                Math.abs(r.delta_pct || 0) > 15 ? "text-red-600 font-semibold" :
                                                Math.abs(r.delta_pct || 0) > 5 ? "text-amber-600" : "text-slate-500"
                                            }`}>{r.delta_pct != null ? `${r.delta_pct}%` : "—"}</td>
                                            <td className="py-2 px-3 text-center">
                                                {r.severity && <SevPill s={r.severity} />}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}

function FiBox({ label, v, pct, tone }) {
    const cls = tone === "amber" ? "text-amber-600" : tone === "emerald" ? "text-emerald-600" : "text-slate-800";
    return (
        <div>
            <div className="eyebrow text-slate-500 mb-0.5">{label}</div>
            <div className={`text-[18px] font-mono font-semibold ${cls}`}>{v != null ? fmtINR(v, 2) : "—"}</div>
            {pct != null && <div className="text-[11px] text-slate-500 font-mono">{pct}%</div>}
        </div>
    );
}

function SevPill({ s }) {
    const map = {
        INFO:     "bg-emerald-50 text-emerald-700 border-emerald-200",
        WARN:     "bg-amber-50 text-amber-700 border-amber-200",
        CRITICAL: "bg-red-50 text-red-700 border-red-200",
    };
    return <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-semibold ${map[s]}`}>{s}</span>;
}

function fmt(v) {
    if (v == null) return "—";
    if (typeof v !== "number") return String(v);
    return v.toLocaleString("en-IN", { maximumFractionDigits: 4 });
}
